import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";
import {
  useGetBaseRateFileAttachedContractsQuery,
  useSetBaseRateFileCarveoutFlagMutation,
} from "store/queries/baseRateFiles";

export interface IConvertibleFile {
  id: string;
  displayName?: string;
  fileName?: string;
  isCarveout?: boolean;
}

interface IToastMessage {
  severity: "success" | "error";
  summary: string;
  detail: string;
  life?: number;
}

interface IUseBaseRateFileKindConversionArgs {
  /** Files whose attachment state should be pre-fetched (one batched request). */
  fileIds: string[];
  /** Hospital account, when the library is embedded in a hospital — scopes contract links. */
  accountId?: string;
  showToast: (message: IToastMessage) => void;
}

interface IOpenConvertDialogOptions {
  /** Extra caution shown in the confirm modal (e.g. losing the only base rate source). */
  warning?: string;
  onConverted?: (isCarveout: boolean) => void;
}

/**
 * CLE-2567 — shared "convert this file between base rate and carve-out" action,
 * used from the base rate file library and from a contract's rate calculations.
 *
 * A file attached to any contract cannot be converted: the two kinds hang off
 * different tables (ContractRateCalculation vs ContractCarveout), so flipping in
 * place would orphan the link. Rather than letting the user confirm and then fail,
 * the attachment is looked up up front and the blocker is shown immediately, with
 * links to the contracts holding the file.
 */
export function useBaseRateFileKindConversion({
  fileIds,
  accountId,
  showToast,
}: IUseBaseRateFileKindConversionArgs) {
  const navigate = useNavigate();
  const [setCarveoutFlag] = useSetBaseRateFileCarveoutFlagMutation();

  const { data: attachedContractsData } =
    useGetBaseRateFileAttachedContractsQuery(
      { ids: fileIds },
      { skip: fileIds.length === 0 }
    );

  const contractsUsingFile = (
    fileId: string
  ): { id: string; label: string }[] =>
    attachedContractsData?.data?.[fileId] ?? [];

  /** Contract detail route, hospital-scoped when embedded in a hospital. */
  const contractUrl = (id: string) =>
    accountId
      ? `/hospitals/view/${accountId}/contracts/${id}`
      : `/payer-rules/contracts/${id}`;

  const openConvertDialog = (
    file: IConvertibleFile,
    options: IOpenConvertDialogOptions = {}
  ) => {
    const toCarveout = !file.isCarveout;
    const target = toCarveout ? "carve-out" : "base rate";
    const label = file.displayName || file.fileName || "This file";
    const attached = contractsUsingFile(file.id);

    if (attached.length > 0) {
      const blocker = confirmDialog({
        message: (
          <div className="flex flex-column gap-2">
            <span>
              <strong>{label}</strong> is attached to {attached.length} contract
              {attached.length === 1 ? "" : "s"} and cannot be converted to a{" "}
              {target} file. Detach it from{" "}
              {attached.length === 1 ? "this contract" : "these contracts"}{" "}
              first:
            </span>
            <ul className="list-none p-0 m-0 flex flex-column gap-1">
              {attached.map((contract) => (
                <li key={contract.id}>
                  <Button
                    label={contract.label}
                    icon="pi pi-external-link"
                    iconPos="right"
                    className="p-button-link p-0"
                    onClick={() => {
                      blocker.hide();
                      navigate(contractUrl(contract.id));
                    }}
                  />
                </li>
              ))}
            </ul>
          </div>
        ),
        header: "Cannot Convert",
        icon: "pi pi-ban",
        acceptLabel: "Close",
        acceptClassName: "p-button-secondary",
        rejectClassName: "hidden",
      });
      return;
    }

    confirmDialog({
      message: (
        <div className="flex flex-column gap-2">
          <span>
            Convert <strong>{label}</strong> to a {target} file? This applies to
            every term and version of {file.fileName ?? label}.
          </span>
          {options.warning && (
            <span className="text-orange-600">{options.warning}</span>
          )}
        </div>
      ),
      header: "Confirm Conversion",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          const result = await setCarveoutFlag({
            id: file.id,
            isCarveout: toCarveout,
          }).unwrap();
          showToast({
            severity: "success",
            summary: "File Converted",
            detail: `Converted to a ${target} file (${
              result.updatedCount
            } version${result.updatedCount === 1 ? "" : "s"} updated).`,
          });
          options.onConverted?.(toCarveout);
        } catch (error: any) {
          showToast({
            severity: "error",
            summary: "Conversion Failed",
            detail:
              error?.data?.messages?.[0] ||
              error?.data?.message ||
              error?.data?.error ||
              `Failed to convert the file to a ${target} file.`,
            life: 10000,
          });
        }
      },
    });
  };

  return { openConvertDialog, contractsUsingFile };
}
